import { useEffect, useMemo, useState } from 'react';
import { Media } from '@weave/schema-gen-ts/dist/schemas/messaging/media/public/v1/service.pb';
import { LogoSection as LogoSectionType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { useListBrandSettings } from '@frontend/api-brand-settings';
import { useAppScopeStore } from '@frontend/scope';
import { SelectImageModal } from '@frontend/select-image-modal';
import { theme } from '@frontend/theme';
import { Modal, useModalControl } from '@frontend/design-system';
import { useContentComposer } from '../../content-composer/content-composer-provider';
import { useComposerSection } from '../../content-composer/hooks/use-composer-section';
import { SectionBase } from '../../content-composer/section-base/section-base';
import type { ComposerSection } from '../../content-composer/types';
import { convertMediaToImage } from '../../utils/convert-media-to-image';
import { JustifiedLayout } from '../atoms/justified-layout';
import { ImageWithDimensions } from '../types';
import { getFrameStyle } from '../utils/get-frame-style';
import { createImagesWithDimensions, newImageWithDimensions, removeDimensions } from '../utils/image-dimensions';
import { LogoOutput } from './logo-output';
import { LogoSettings } from './logo-settings';
import { LogoTool } from './logo-tool';

export const LogoSection: ComposerSection<LogoSectionType> = ({ corners, frame, height, image }) => {
  const { selectedOrgId: orgId } = useAppScopeStore();
  const { locationIds, selectedLocationId } = useContentComposer();

  const { data: settings } = useListBrandSettings({
    orgId,
    locationIds: selectedLocationId ? [selectedLocationId] : locationIds,
  });

  const logoImage = useMemo(() => {
    if (!selectedLocationId) return image;
    const brandSetting = settings?.[selectedLocationId];
    if (brandSetting?.logoUrl) {
      return {
        name: '',
        url: brandSetting.logoUrl,
      };
    }
    return image;
  }, [image, selectedLocationId, settings]);

  const [logoWithDimensions, setLogoWithDimensions] = useState(() =>
    createImagesWithDimensions(1, logoImage ? [logoImage] : [])
  );
  const [isLogoSelected, setIsLogoSelected] = useState(false);
  const { setProps } = useComposerSection<LogoSectionType>();
  const { modalProps, triggerProps } = useModalControl();

  useEffect(() => {
    setLogoWithDimensions(createImagesWithDimensions(1, logoImage ? [logoImage] : []));
  }, [logoImage]);

  const loadLogo = (imageIndex: number, imageWidth: number, imageHeight: number) => {
    setLogoWithDimensions((previousLogoWithDimensions) => {
      const updatedLogoWithDimensions = [...previousLogoWithDimensions];
      updatedLogoWithDimensions[imageIndex] = {
        ...newImageWithDimensions(updatedLogoWithDimensions[imageIndex]),
        width: imageWidth,
        height: imageHeight,
      };
      return updatedLogoWithDimensions;
    });
  };

  const saveLogo = (updatedLogoWithDimensions: ImageWithDimensions) => {
    const updatedLogo = removeDimensions(updatedLogoWithDimensions);
    setProps((props) => (props.image = updatedLogo));
    setLogoWithDimensions([updatedLogoWithDimensions]);
  };

  const deselectLogo = () => {
    setIsLogoSelected(false);
  };

  const selectLogo = () => {
    setIsLogoSelected(true);
  };

  const removeLogo = () => {
    deselectLogo();
    saveLogo(newImageWithDimensions());
  };

  const startReplaceLogo = () => {
    selectLogo();
    triggerProps.onClick();
  };

  const finishReplaceLogo = (media?: Media) => {
    if (!media) return;
    const updatedLogoWithDimensions = newImageWithDimensions(convertMediaToImage(media));
    saveLogo(updatedLogoWithDimensions);
    modalProps.onClose();
  };

  return (
    <SectionBase onClick={deselectLogo} css={getFrameStyle(frame)} disableCopy>
      <JustifiedLayout
        alignment={frame?.alignment}
        corners={corners}
        height={height}
        images={logoWithDimensions}
        onLoad={loadLogo}
        onRemove={removeLogo}
        onReplace={startReplaceLogo}
        onSelect={selectLogo}
        selectedImageIndex={isLogoSelected ? 0 : undefined}
        type='logo'
      />
      <Modal {...modalProps} minWidth={700}>
        <SelectImageModal
          locationIds={locationIds}
          modalProps={modalProps}
          onSelect={finishReplaceLogo}
          showLocationFilter
        />
      </Modal>
    </SectionBase>
  );
};

LogoSection.craft = {
  props: {
    frame: {
      alignment: 'center',
      backgroundColor: theme.colors.neutral5.toUpperCase(),
      paddingBottom: '24px',
      paddingLeft: '24px',
      paddingRight: '24px',
      paddingTop: '24px',
    },
    height: '64px',
    sectionType: 'LogoSection',
  },
  related: {
    output: LogoOutput,
    settings: LogoSettings,
    tool: LogoTool,
  },
};
